import { generatePath } from 'react-router-dom';

import { EmptyState } from '@components/empty-state';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { ListCard } from '@components/list-card';
import { PageContainer } from '@components/page-containers';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';

import { FormTemplateJourney } from 'api/forms/requests';
import { routePaths } from 'ui/authenticated/navigation';

const JourneyTag = ({ status }: { status: FormTemplateJourney['status'] }) => {
  const { t } = useScopedTranslate(locale);
  switch (status) {
    case 'published':
      return <Tag variant="green" size="large" text={t('published')} />;
    case 'unpublished':
      return <Tag variant="indigo" size="large" text={t('unpublished')} />;
    case 'old':
      return <Tag variant="empty" size="large" text={t('old')} />;
    default:
      return null;
  }
};

export const JourneysTabPage = ({ journeys = [] }: { journeys?: FormTemplateJourney[] }) => {
  const { t } = useScopedTranslate(locale);

  const hasJourneys = journeys.length > 0;

  return (
    <PageContainer.Page>
      <PageContainer.PageContent>
        {hasJourneys ? (
          <Stack space={2}>
            {journeys.map((journey) => (
              <ListCard
                key={journey.shortId}
                to={generatePath(routePaths.journeyDetails, {
                  workspaceId: journey.workspace,
                  journeyId: journey.shortId,
                  '*': null,
                })}
                title={journey.title}
                description={journey.description}
                icon={<IconFancyContainer icon={<IconJourneyItem />} />}
                actions={
                  <>
                    <Stack orientation="horizontal" align="center">
                      <JourneyTag status={journey.status} />
                    </Stack>
                    <ValueList
                      hasBorder
                      items={[
                        { value: journey.numTotalTravellers, label: t('travellers') },
                        { value: journey.numNodeActions, label: t('actions') },
                      ]}
                    />
                  </>
                }
              />
            ))}
          </Stack>
        ) : (
          <EmptyState
            skipMargin
            header={t('noJourneys')}
            content={t('noJourneysDescription')}
            icon={<IconJourneyItem size={80} />}
          />
        )}
      </PageContainer.PageContent>
    </PageContainer.Page>
  );
};

const locale = {
  en: {
    noJourneys: 'No journeys',
    noJourneysDescription: 'This form is currently not used in any journeys',
    travellers: 'Travellers',
    actions: 'Actions',
    published: 'Published',
    unpublished: 'Unpublished',
    old: 'Old version',
  },
};
